import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Copy, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { PERMISSION_DEFS } from "@/components/team/permissionDefs";

export function InviteForm({ role, onClose, onRefresh }) {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const toggle = (key) => setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  const defs = PERMISSION_DEFS[role] || PERMISSION_DEFS.gerente;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error(t("team.nameRequired")); return; }
    if (!email.trim()) { toast.error(t("team.emailRequired")); return; }
    setLoading(true);
    try {
      const res = await authAxios.post("/deliverers/team/members", { name, email, phone: phone.trim() || undefined, role, permissions });
      setInviteResult(res.data);
      if (res.data.email_sent) {
        toast.success(t("team.inviteSentEmail"));
      } else if (res.data.email_error) {
        toast.warning(`${t("team.inviteEmailFailed")}: ${res.data.email_error}`);
      } else {
        toast.success(t("team.inviteCreatedNoEmail"));
      }
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("team.inviteError")); }
    setLoading(false);
  };

  const inviteUrl = inviteResult ? `${window.location.origin}/invite/${inviteResult.invite_token}` : "";
  const copyInviteLink = () => { navigator.clipboard.writeText(inviteUrl); toast.success(t("team.linkCopied")); };

  if (inviteResult) {
    return (
      <div className="space-y-4" data-testid="invite-result">
        <div className="text-center py-4">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold">{t("team.inviteCreatedTitle")}</h3>
          {inviteResult.email_sent ? (
            <p className="text-sm text-emerald-600 mt-1">{t("team.emailSentTo")} <span className="font-bold">{email}</span></p>
          ) : (
            <p className="text-sm text-amber-600 mt-1">
              {inviteResult.email_error ? `${t("team.emailFailedPrefix")} ${inviteResult.email_error}` : t("team.sendManually")}
            </p>
          )}
          {phone.trim() && (
            <a
              href={`https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Olá ${name}! Aqui está o seu convite de acesso:\n${inviteUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-3 w-full py-2 px-4 rounded-lg bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5a] transition"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.523 5.847L.057 23.886a.5.5 0 0 0 .611.611l6.054-1.463A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.86 0-3.6-.5-5.1-1.37l-.365-.213-3.787.915.935-3.737-.237-.385A9.939 9.939 0 0 1 2 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"/></svg>
              {t("team.sendOnWhatsapp")}
            </a>
          )}
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <Label className="text-xs uppercase text-gray-500">{t("team.inviteLink")}</Label>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 text-xs bg-white border rounded px-2 py-2 break-all">{inviteUrl}</code>
            <Button variant="outline" size="icon" onClick={copyInviteLink} data-testid="copy-invite-link"><Copy className="w-4 h-4" /></Button>
          </div>
        </div>
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={onClose}>{t("team.close")}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>{t("team.inviteNameLabel")}</Label><Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" data-testid="invite-name" /></div>
        <div><Label>{t("team.inviteEmailLabel")}</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1" data-testid="invite-email" /></div>
        <div className="col-span-2"><Label>{t("team.inviteWhatsappLabel")}</Label><Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+34612345678" className="mt-1" data-testid="invite-phone" /></div>
      </div>

      <Separator />
      <h4 className="font-bold text-sm uppercase text-gray-700">{t("team.permissions")}</h4>

      <ScrollArea className="max-h-[380px]">
        <div className="space-y-4 pr-3">
          {defs.map(cat => (
            <div key={cat.category}>
              <p className="text-xs font-bold uppercase text-emerald-700 mb-2">{cat.category}</p>
              <div className="space-y-2">
                {cat.perms.map(p => (
                  <div key={p.key} className="flex items-center justify-between py-1">
                    <span className="text-sm text-gray-700">{p.label}</span>
                    <Switch checked={!!permissions[p.key]} onCheckedChange={() => toggle(p.key)} data-testid={`perm-${p.key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>{t("team.cancel")}</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="send-invite-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4" /> {t("team.sendInvitation")}</>}
        </Button>
      </DialogFooter>
    </form>
  );
}
